import React, { useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useNavigation, useConfig } from 'msp-integrations'
import {
  DynamicTag,
  PasswordSecurity,
  AlertBox,
  Spinner,
  Button,
  Field
} from '../../../index'
import { usePasswordChangeField } from './AccountData.helpers'

const onCustomerPasswordChange = async (
  data: any,
  email: string,
  fieldConfig: any,
  setError: (id: string, value: object) => void,
  formatMessage: any,
  updatePassword: (
    data: { password: string; currentPassword: string },
    email: string
  ) => void,
  navigate: (string) => void,
  setAlertBoxMessage: (data: {
    type: string
    message: string
    readOnly: boolean
  }) => void,
  alertBoxRef: any
) => {
  const checkPasswordStrength = (password) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[+@$^!%*?&()])[A-Za-z\d@$^+!%*?&()]{8,}$/
    return passwordRegex.test(password)
  }

  try {
    if (data.password !== data.repeatPassword) {
      setError(fieldConfig.password.name, {
        type: 'manual',
        message: formatMessage({
          defaultMessage: 'Passwords do not match.',
          id: 'msp.register_form.error.password_match'
        })
      })

      setError(fieldConfig.repeatPassword.name, {
        type: 'manual',
        message: formatMessage({
          defaultMessage: 'Passwords do not match.',
          id: 'msp.register_form.error.password_match'
        })
      })

      return
    } else if (!checkPasswordStrength(data.password)) {
      setError(fieldConfig.password.name, {
        type: 'manual',
        message: formatMessage({
          defaultMessage: "Password requirement doesn't meet.",
          id: 'msp.register_form.error.password_requirement'
        })
      })

      setError(fieldConfig.repeatPassword.name, {
        type: 'manual',
        message: formatMessage({
          defaultMessage: "Password requirement doesn't meet.",
          id: 'msp.register_form.error.password_requirement'
        })
      })
      return
    }

    try {
      await updatePassword(
        {
          password: data.password,
          currentPassword: data.currentPassword
        },
        email
      )
      setAlertBoxMessage({
        type: 'success',
        readOnly: true,
        message: formatMessage({
          defaultMessage: 'Your password has been updated successfully.',
          id: 'msp.account.password.updated'
        })
      })
      alertBoxRef?.current?.showAlert()
      setTimeout(() => navigate('/account'), 2000)
    } catch (e: any) {
      setAlertBoxMessage({
        type: 'error',
        readOnly: false,
        message:
          e?.message ||
          formatMessage({
            defaultMessage:
              'An error occurred updated your password. Please, try again.',
            id: 'msp.account.password.updated.fail'
          })
      })
      alertBoxRef?.current?.showAlert()
    }
  } catch (error) {
    setError('global', { type: 'manual', message: error.message })
    alertBoxRef?.current?.showAlert()
  }
}

export const PasswordChangeForm = ({
  updatePassword,
  email
}: {
  updatePassword: (
    data: { password: string; currentPassword: string },
    email: string
  ) => void
  email: string
}) => {
  const fieldConfig = usePasswordChangeField()
  const [alertBoxMessage, setAlertBoxMessage] = useState({
    type: 'error',
    readOnly: false,
    message: ''
  })
  const {
    theme: { font }
  } = useConfig()
  const navigate = useNavigation()
  const alertBoxRef = useRef<typeof AlertBox>()
  const {
    control,
    errors,
    formState,
    handleSubmit,
    setError,
    clearErrors,
    watch
  } = useForm()

  const { formatMessage } = useIntl()
  const watchPwdField = watch(fieldConfig.password.name)
  return (
    <>
      <AlertBox
        readOnly={alertBoxMessage.readOnly}
        ref={alertBoxRef}
        type={alertBoxMessage.type}
        title={alertBoxMessage.message}
      />
      <form
        onSubmit={handleSubmit((data) =>
          onCustomerPasswordChange(
            data,
            email,
            fieldConfig,
            setError,
            formatMessage,
            updatePassword,
            navigate,
            setAlertBoxMessage,
            alertBoxRef
          )
        )}
      >
        <DynamicTag
          componentTagName='Headline'
          tag='h6'
          className={`${font.medium} mb-4 text-black`}
        >
          {formatMessage({
            id: 'msp.account.mydata.password',
            defaultMessage: 'Change Password'
          })}
        </DynamicTag>
        <Field
          className={'w-full'}
          control={control}
          config={fieldConfig.currentPassword}
          error={errors?.currentPassword}
          isDisabled={formState.isSubmitting}
        />
        <Field
          className={'w-full'}
          control={control}
          config={fieldConfig.password}
          error={errors?.password}
          isDisabled={formState.isSubmitting}
        />
        <Field
          className={'w-full'}
          control={control}
          config={fieldConfig.repeatPassword}
          error={errors?.repeatPassword}
          isDisabled={formState.isSubmitting}
        />
        <PasswordSecurity password={watchPwdField} />
        <Button
          className='my-4 w-full lg:w-6/12'
          onClick={() => clearErrors()}
          iconName={!formState.isSubmitting ? 'ArrowRight' : ''}
          iconPosition='right'
          isDisabled={formState.isSubmitting}
          isLoading={formState.isSubmitting}
          isSubmit
          loadingContent={<Spinner size='xl' />}
        >
          {formatMessage({
            defaultMessage: 'Save changes',
            id: 'msp.account.mydata.save.changes'
          })}
        </Button>
      </form>
    </>
  )
}
